import React, { useState } from "react";
import classNames from "classnames";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import { UserMenuOptions } from "./UserMenuOptions";
import { LanguagesSelector } from "./LanguageSelector";
import OutsideClickDetector from "@components-shared/OutsideClickDetector";
import ThemeToggleButton from "@components-shared/ThemeToggleButton";
import { top, bottom } from "safe-area-insets";
import { CapacitorUpdate } from "./CapacitorUpdate";
import { CapacitorBundleVersion } from "./CapacitorBundleVersion";
import { isLoginFlow } from "@utils/membership/membershipUtils";
import { DefaultPath } from "@utils/common/defaultRoutePathsEnum";

import { history } from "@state";
import { useRootAppStore } from "@hooks";
import { THEME_SETTINGS } from "@theme-settings";
import { useAppContext, useFixBody } from "@state/hooks/shared";

export default observer(function UserMenu(props: {
	isNavMenuOpen?: boolean;
	setIsNavMenuOpen: (value: boolean) => void;
}) {
	const { isNavMenuOpen, setIsNavMenuOpen } = props;
	const [isLanguagesSelectorOpen, setIsLanguagesSelectorOpen] =
		useState<boolean>(false);

	const { t } = useTranslation();
	const { pathname } = useLocation();
	const { culture } = useAppContext();
	const date = new Date();
	const rootStore = useRootAppStore();

	useFixBody(true, isNavMenuOpen);

	const sidebarClassName = classNames("l--aside", {
		"is-opened": isNavMenuOpen,
	});

	const logout = async () => {
		await rootStore.userAuthStore.logoutUser();
		history.push(`/${culture}/${DefaultPath.HOME}`);
		setIsNavMenuOpen(false);
	};

	if (isLoginFlow(pathname)) {
		return (
			<aside className={sidebarClassName} style={{ top, bottom }}>
				<div className="l--aside__overlay" />
				<OutsideClickDetector
					wrapperClasses="sidebar"
					onOutsideClick={() => {
						setIsNavMenuOpen(false);
					}}
					style={{ top, bottom }}
				>
					<>
						<CapacitorUpdate setIsNavMenuOpen={setIsNavMenuOpen} />
						<div className="sidebar__section">
							<div className="sidebar__title">
								<div className="title--sidebar">
									{t<string>("SUBHEADER.PREFERENCES")}
								</div>
							</div>
							<nav className="nav--secondary">
								<ul className="nav--secondary__menu">
									<li className="nav--secondary__item">
										<button
											className="nav--secondary__link"
											type="button"
											onClick={() =>
												setIsLanguagesSelectorOpen(true)
											}
										>
											<span className="nav--secondary__inner">
												<span>
													{t<string>(
														"SUBHEADER.LANGUAGES.LABEL"
													)}
												</span>
												<small className="nav--secondary__description">
													{t<string>(
														"SUBHEADER.LANGUAGES.CONTENT"
													)}
												</small>
											</span>
											<i className="nav--secondary__caret u-icon u-icon--tny u-icon--caret--right" />
										</button>
									</li>
									{THEME_SETTINGS.isThemeSelectorEnabled && (
										<li className="nav--secondary__item">
											<div className="nav--secondary__link hover-off">
												<div className="nav--secondary__inner">
													<div>
														{t<string>(
															"SUBHEADER.THEME_SELECTOR.LABEL"
														)}
													</div>
													<small className="nav--secondary__description">
														{t<string>(
															"HEADER.THEME_LABEL"
														)}
													</small>
												</div>
												<ThemeToggleButton />
											</div>
										</li>
									)}
								</ul>
							</nav>
						</div>

						<div className="sidebar__section sidebar__section--social">
							<nav className="nav--secondary nav--inline">
								<ul className="nav--inline">
									<li className="nav--inline__item">
										<a
											href="https://www.facebook.com/TipwinSport/"
											className="nav--inline__link"
											rel="noreferrer"
											target="_blank"
										>
											<i className="u-icon u-icon--big u-icon--facebook" />
										</a>
									</li>
									<li className="nav--inline__item">
										<a
											href="https://twitter.com/TipWinCompany"
											className="nav--inline__link"
											rel="noreferrer"
											target="_blank"
										>
											<i className="u-icon u-icon--big u-icon--twitter" />
										</a>
									</li>
									<li className="nav--inline__item">
										<a
											href="https://www.instagram.com/tipwinofficial/"
											className="nav--inline__link"
											rel="noreferrer"
											target="_blank"
										>
											<i className="u-icon u-icon--big u-icon--instagram" />
										</a>
									</li>
								</ul>
							</nav>
						</div>

						<div className="sidebar__section sidebar__section--copyright">
							<div>
								&#169;
								{t<string>(
									"specific:FOOTER.ALL_RIGHTS_RESERVED",
									{
										0: date.getFullYear(),
									}
								)}{" "}
								{t<string>(
									"specific:FOOTER.FOOTER_RIGHTS_RESERVED"
								)}
							</div>
						</div>
						<LanguagesSelector
							setIsLanguagesSelectorOpen={
								setIsLanguagesSelectorOpen
							}
							isLanguagesSelectorOpen={isLanguagesSelectorOpen}
						/>
					</>
				</OutsideClickDetector>
			</aside>
		);
	}

	return (
		<aside className={sidebarClassName} style={{ top, bottom }}>
			<div className="l--aside__overlay" />
			<OutsideClickDetector
				wrapperClasses="sidebar"
				onOutsideClick={() => {
					setIsNavMenuOpen(false);
				}}
				style={{ top, bottom }}
			>
				<>
					<UserMenuOptions
						logout={logout}
						setIsNavMenuOpen={setIsNavMenuOpen}
					/>

					<CapacitorUpdate setIsNavMenuOpen={setIsNavMenuOpen} />
					<div className="sidebar__section">
						<div className="sidebar__title">
							<div className="title--sidebar">
								{t<string>("SUBHEADER.PREFERENCES")}
							</div>
						</div>
						<nav className="nav--secondary">
							<ul className="nav--secondary__menu">
								<li className="nav--secondary__item">
									<button
										className="nav--secondary__link"
										type="button"
										onClick={() =>
											setIsLanguagesSelectorOpen(true)
										}
									>
										<span className="nav--secondary__inner">
											<span>
												{t<string>(
													"SUBHEADER.LANGUAGES.LABEL"
												)}
											</span>
											<small className="nav--secondary__description">
												{t<string>(
													"SUBHEADER.LANGUAGES.CONTENT"
												)}
											</small>
										</span>
										<i className="nav--secondary__caret u-icon u-icon--tny u-icon--caret--right" />
									</button>
								</li>
								{THEME_SETTINGS.isThemeSelectorEnabled && (
									<li className="nav--secondary__item">
										<div className="nav--secondary__link hover-off">
											<div className="nav--secondary__inner">
												<div>
													{t<string>(
														"SUBHEADER.THEME_SELECTOR.LABEL"
													)}
												</div>
												<small className="nav--secondary__description">
													{t<string>(
														"HEADER.THEME_LABEL"
													)}
												</small>
											</div>
											<ThemeToggleButton />
										</div>
									</li>
								)}
							</ul>
						</nav>
					</div>

					<div className="sidebar__section">
						<div className="sidebar__title">
							<div className="title--sidebar">
								{t<string>("FOOTER.COMPANY")}
							</div>
						</div>
						<nav className="nav--secondary">
							<ul className="nav--secondary__menu">
								<li
									className="nav--secondary__item"
									onClick={() => setIsNavMenuOpen(false)}
								>
									<Link
										to={`/${culture}/info`}
										className="nav--secondary__link"
									>
										<div className="nav--secondary__inner">
											<div>{t<string>("LIVE.INFO")}</div>
											<small className="nav--secondary__description">
												{t<string>(
													"HEADER.ABOUT_US_AND_LEGAL_INFORMATION"
												)}
											</small>
										</div>
										<i className="nav--secondary__caret u-icon u-icon--tny u-icon--caret--right" />
									</Link>
								</li>
								<li
									className="nav--secondary__item"
									onClick={() => setIsNavMenuOpen(false)}
								>
									<Link
										to={`/${culture}/gaming-risks`}
										className="nav--secondary__link"
									>
										<div className="nav--secondary__inner">
											<div>
												{t<string>(
													"GAMING_RISKS.TITLE"
												)}
											</div>
											<small className="nav--secondary__description">
												{t<string>(
													"GAMING_RISKS.SUBTITLE"
												)}
											</small>
										</div>
										<i className="nav--secondary__caret u-icon u-icon--tny u-icon--caret--right" />
									</Link>
								</li>
								<li
									className="nav--secondary__item"
									onClick={() => setIsNavMenuOpen(false)}
								>
									<Link
										to={`/${culture}/info/contact`}
										className="nav--secondary__link"
									>
										<div className="nav--secondary__inner">
											<div>
												{t<string>("COMMON.CONTACT")}
											</div>
											<small className="nav--secondary__description">
												{t<string>("HEADER.CONTACT_US")}
											</small>
										</div>
										<i className="nav--secondary__caret u-icon u-icon--tny u-icon--caret--right" />
									</Link>
								</li>
							</ul>
						</nav>
					</div>

					<div className="sidebar__section sidebar__section--social">
						<nav className="nav--secondary nav--inline">
							<ul className="nav--inline">
								<li className="nav--inline__item">
									<a
										href="https://www.facebook.com/TipwinSport/"
										className="nav--inline__link"
										rel="noreferrer"
										target="_blank"
									>
										<i className="u-icon u-icon--big u-icon--facebook" />
									</a>
								</li>
								<li className="nav--inline__item">
									<a
										href="https://twitter.com/TipWinCompany"
										className="nav--inline__link"
										rel="noreferrer"
										target="_blank"
									>
										<i className="u-icon u-icon--big u-icon--twitter" />
									</a>
								</li>
								<li className="nav--inline__item">
									<a
										href="https://www.instagram.com/tipwinofficial/"
										className="nav--inline__link"
										rel="noreferrer"
										target="_blank"
									>
										<i className="u-icon u-icon--big u-icon--instagram" />
									</a>
								</li>
							</ul>
						</nav>
					</div>

					<div className="sidebar__section sidebar__section--copyright">
						<div>
							&#169;{" "}
							{t<string>("specific:FOOTER.ALL_RIGHTS_RESERVED", {
								0: date.getFullYear(),
							})}
						</div>
						<div>
							{t<string>(
								"specific:FOOTER.FOOTER_RIGHTS_RESERVED"
							)}
						</div>
						<CapacitorBundleVersion />
					</div>
					<LanguagesSelector
						setIsLanguagesSelectorOpen={setIsLanguagesSelectorOpen}
						isLanguagesSelectorOpen={isLanguagesSelectorOpen}
					/>
				</>
			</OutsideClickDetector>
		</aside>
	);
});
